import styles from "../../assets/styles/Wallet/Wallet.module.scss";
import React, { useMemo } from "react";
import {
  AppPage,
  BusinessInsightComponent as BIC,
  PageLoader,
  MimaButton,
  MimaTable,
} from "../../components";
import { amountFormatter } from "../../utils/utils";
import moment from "moment";
import { AnimatePresence } from "framer-motion";
import {
  CreateInflowModalContainer,
  InflowReviewModalContainer,
} from "../../containers";
import constant from "../../utils/constant";
import ViewInflowModal from "./ViewInflowModal";
import { downloadXlsx2 } from "../../utils/download";

const CreateInflow = ({
  closeModal,
  createInflowModal,
  goToCreateInflow,
  isLoading,
  data,
  isFetching,
  onLimitChange,
  limit,
  setSearchQuery,
  currentPage,
  setCurrentPage,
  goToReviewInflow,
  reviewInflowModal,
  goToViewInflowDetails,
  viewInflowModal,
}) => {
  const tableData = useMemo(() => {
    if (data?.status === constant.Success) {
      return data?.data?.value.filter((X) => X.type === "INFLOW") || [];
    }
    return [];
  }, [data]);

  const tableColumns = [
    {
      Header: "Creation Date",
      accessor: "createdAt",
      Cell: ({ cell }) => {
        const createdAt = cell.row.original.createdAt;
        const formattedCreatedAt =
          moment(createdAt).format("DD/MM/YYYY HH:MM A");
        return <span>{formattedCreatedAt}</span>;
      },
    },
    {
      Header: "Business Name",
      accessor: "tradingName",
      Cell: ({ cell }) => {
        const businessName = cell.row.original.business?.tradingName;
        return <span>{businessName}</span>;
      },
    },
    {
      Header: "Amount",
      accessor: "amountPaid",
      Cell: ({ cell }) => {
        const amount = cell.row.original.amountPaid;
        const currencyCode = cell.row.original.currencyCode;
        const formattedAmount = amountFormatter(currencyCode).format(amount);
        return <span>{formattedAmount}</span>;
      },
    },
    {
      Header: "Vendor",
      accessor: "vendor",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ cell }) => {
        const status = cell.row.original.status;
        const styleClass = `status ${status === "FULLY-PAID" || status === "PAID" ? "status__green" : status === "PENDING" ? "status__dark__yellow" : status === "VOID" || status === "REVERSED" ? "status__grey" : "status__dark__yellow"}`;
        return <span className={styleClass}>{status}</span>;
      },
    },
    {
      Header: "Approved By",
      accessor: "updatedBy",
      Cell: ({ cell }) => {
        const updatedBy = cell.row.original.updatedBy;
        const updatedAt = cell.row.original.updatedAt;
        const formattedUpdatedAt =
          moment(updatedAt).format("DD/MM/YYYY HH:MM A");
        return (
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600 }}>
              {updatedBy?.firstname
                ? `${updatedBy?.firstname} ${updatedBy?.lastname} `
                : "N/A"}
            </span>
            {updatedBy?.firstname && <span>{formattedUpdatedAt}</span>}
          </span>
        );
      },
    },
    {
      Header: "Mima Payment Channel",
      accessor: "invoice",
      Cell: ({ cell }) => {
        const invoice = cell.row.original.invoice;
        const viaFashWebsite = invoice?.orders?.[0]?.style;
        const createdManually = cell.row.original?.createdBy?._id;
        const chargeSplit = cell.row.original?.chargeSplit;
        const channelColor = viaFashWebsite
          ? "var(--color-primary)"
          : createdManually
            ? "var(--color-yellow-3)"
            : "var(--color-success)";
        return (
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600, color: channelColor }}>
              {viaFashWebsite
                ? `Fash Website Order`
                : createdManually
                  ? `Created Manually`
                  : "invoice or Payment link"}
            </span>
            <span>{chargeSplit ? `Charge Split: ${chargeSplit}` : ""}</span>
          </span>
        );
      },
    },
    {
      Header: "Action",
      Cell: ({ cell }) => {
        const item = cell.row.original;
        const status = cell.row.original.status;

        return (
          <>
            {status === "PENDING" && (
              <span
                className="tableAction__text"
                onClick={() => {
                  goToReviewInflow(item);
                }}
              >
                Review Inflow
              </span>
            )}
            {status !== "PENDING" && (
              <span
                className="tableAction__text"
                onClick={() => {
                  goToViewInflowDetails(item);
                }}
              >
                View Details
              </span>
            )}
          </>
        );
      },
    },
  ];

  const printData = tableData.map((item) => {
    const totalInvoicePrice = item?.invoice?.orders?.reduce((total, order) => {
      return total + order.quantity * order.unitPrice;
    }, 0);

    const viaFashWebsite = item?.invoice?.orders?.[0]?.style;
    const createdManually = item?.createdBy?._id;
    const createdBy = item?.createdBy;
    const updatedBy = item?.updatedBy;
    const updatedAt = item?.updatedAt;

    return {
      "Created Date": moment(item.createdAt).format("DD/MM/YY HH:mm"),
      "Business Name": item?.business?.tradingName || "",
      Narration:
        item?.narration || `Payment for Invoice ${item?.invoiceNumber}` || "",
      "Currency Code": item?.currencyCode || "",
      "Total Amount Paid": amountFormatter(item.currencyCode).format(
        item.amountPaid,
      ),
      "Total Invoice Price": !createdManually
        ? amountFormatter(item.currencyCode).format(totalInvoicePrice ?? 0)
        : "N/A",
      "Charge Split": item?.chargeSplit || "",
      Status: item?.status || "",
      Vendor: item?.vendor || "",
      "Payment Channel": viaFashWebsite
        ? `Fash Website Order`
        : createdManually
          ? `Created Manually`
          : "invoice or Payment link",
      "Created By": createdBy
        ? `${createdBy?.firstname} ${createdBy?.lastname}`
        : "N/A",
      "Approved By": updatedBy
        ? `${updatedBy?.firstname} ${updatedBy?.lastname}`
        : "N/A",
      "Approved Date": updatedAt
        ? moment(updatedAt).format("DD/MM/YY HH:mm")
        : "",
    };
  });
  return (
    <AppPage pageTitle="Create Inflow">
      {isLoading ? (
        <PageLoader title="While we set things up for you" />
      ) : (
        <>
          <div className={styles.cards}>
            <BIC
              title="Inflow Count"
              amount={tableData.length}
              styleClass="cards_blue_1"
            />
          </div>
        </>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "1rem",
          flexWrap: "wrap",
          marginTop: "5rem",
        }}
      >
        <MimaButton title="Create Inflow" onClick={goToCreateInflow} />
        <MimaButton
          title={`Download Inflow Report`}
          width={30}
          onClick={() => {
            downloadXlsx2(
              printData,
              `Inflow Report ${moment().format("DD/MM/YYYY")}`,
            );
          }}
          variant="outlined"
        />
      </div>
      <MimaTable
        tableData={tableData}
        tableColumns={tableColumns}
        searchPlaceholder="Search Inflow"
        searchVariant="wide"
        totalItems={data?.data?.totalCounts}
        onLimitChange={onLimitChange}
        isLoading={isLoading}
        isFetching={isFetching}
        limit={limit}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setSearchQuery={setSearchQuery}
      />

      {(createInflowModal || reviewInflowModal || viewInflowModal) && (
        <div className="modal">
          <AnimatePresence>
            {createInflowModal && (
              <CreateInflowModalContainer closeModal={closeModal} />
            )}
            {reviewInflowModal && (
              <InflowReviewModalContainer closeModal={closeModal} />
            )}
            {viewInflowModal && <ViewInflowModal closeModal={closeModal} />}
          </AnimatePresence>
        </div>
      )}
    </AppPage>
  );
};

export default CreateInflow;
